import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Sparkles, Check, PenTool } from "lucide-react";
import { WORLDS, AUTHOR_STUDIO } from "@/data";
import { useGame, worldBreakdown } from "@/context/GameContext";
import { sound } from "@/lib/sound";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const CompletionPill = () => {
  const { state, completion } = useGame();
  const r = 15;
  const c = 2 * Math.PI * r;
  return (
    <div className="group relative" data-testid="completion-ring">
      <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-2.5 py-1.5 backdrop-blur-md">
        <div className="relative h-9 w-9">
          <svg width="36" height="36" className="-rotate-90">
            <circle cx="18" cy="18" r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
            <circle
              cx="18"
              cy="18"
              r={r}
              fill="none"
              stroke="#FF2A6D"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={c}
              strokeDashoffset={c - (c * completion.pct) / 100}
              style={{ transition: "stroke-dashoffset 0.6s ease" }}
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[0.6rem] font-bold text-white">
            {completion.pct}%
          </span>
        </div>
        <span className="hidden pr-1 text-xs text-white/70 sm:block">Explorer</span>
      </div>
      <div className="pointer-events-none absolute left-0 top-full z-30 mt-2 w-56 rounded-2xl border border-white/10 bg-black/90 p-3 opacity-0 backdrop-blur-xl transition-opacity duration-300 group-hover:opacity-100">
        <p className="mb-2 text-[0.65rem] uppercase tracking-widest text-white/40">Progress by world</p>
        {WORLDS.map((w) => {
          const b = worldBreakdown(state, w.id, w.characters.map((ch) => ch.id));
          return (
            <div key={w.id} className="mb-2 last:mb-0">
              <div className="flex justify-between text-xs">
                <span style={{ color: w.accent }}>{w.name}</span>
                <span className="text-white/55">{b.pct}%</span>
              </div>
              <div className="mt-1 h-1 rounded-full bg-white/10">
                <div className="h-full rounded-full" style={{ width: `${b.pct}%`, background: w.accent }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FinalCinematic = ({ onClose, onEnter }) => (
  <motion.div
    className="fixed inset-0 z-[140] flex flex-col items-center justify-center overflow-hidden bg-black/85 px-6 text-center backdrop-blur-md"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    data-testid="final-cinematic"
  >
    {Array.from({ length: 42 }).map((_, i) => {
      const a = (Math.PI * 2 * i) / 42;
      const col = ["#FF2A6D", "#B14EDB", "#05D9E8"][i % 3];
      return (
        <motion.span
          key={i}
          className="absolute h-2 w-2 rounded-full"
          style={{ background: col, boxShadow: `0 0 10px ${col}` }}
          initial={{ x: Math.cos(a) * 620, y: Math.sin(a) * 620, opacity: 0 }}
          animate={{ x: 0, y: 0, opacity: [0, 1, 0] }}
          transition={{ duration: 2.2, delay: i * 0.02, repeat: Infinity, repeatDelay: 1 }}
        />
      );
    })}
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", stiffness: 150, damping: 14, delay: 0.2 }}
      className="relative z-10"
    >
      <Sparkles className="mx-auto h-12 w-12 text-[#FFD166]" />
      <p className="kr mt-6 max-w-xl text-xl leading-relaxed text-white">{AUTHOR_STUDIO.finalKr}</p>
      <div className="mt-9 flex flex-wrap justify-center gap-3">
        <button
          onClick={onClose}
          className="rounded-full border border-white/20 px-5 py-2.5 text-sm text-white/80 transition-colors hover:text-white"
        >
          Stay in the lobby
        </button>
        <button
          onClick={onEnter}
          data-testid="enter-studio-cinematic"
          className="rounded-full bg-[#FFD166] px-6 py-2.5 text-sm font-medium text-black transition-transform hover:scale-105"
        >
          Enter the Author's Studio
        </button>
      </div>
    </motion.div>
  </motion.div>
);

const Lobby = ({ onEnterWorld, onEnterStudio }) => {
  const { state, markCinematicSeen } = useGame();
  const showCinematic = state.authorUnlocked && !state.cinematicSeen;

  return (
    <motion.div
      key="lobby"
      className="wv-hero-bg relative min-h-screen w-full overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      data-testid="lobby-screen"
    >
      <motion.div
        className="wv-blob"
        style={{ background: "#FF2A6D", left: "-8%", top: "-6%" }}
        animate={{ x: [0, 60, 0], y: [0, 40, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="wv-blob"
        style={{ background: "#B14EDB", right: "-6%", top: "10%" }}
        animate={{ x: [0, -50, 0], y: [0, 50, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="wv-blob"
        style={{ background: "#05D9E8", left: "30%", bottom: "-12%" }}
        animate={{ x: [0, 40, 0], y: [0, -30, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="wv-grain" />

      {/* nav: logo + completion ring (controls float top-right via TopControls) */}
      <nav className="relative z-20 flex items-center gap-4 px-6 py-6 sm:px-10">
        <div className="flex items-center gap-2 text-white/90">
          <Sparkles className="h-5 w-5" style={{ color: "#FF2A6D" }} />
          <span className="font-display text-sm tracking-[0.3em]">WEBTOONVERSE</span>
        </div>
        <CompletionPill />
      </nav>

      {/* hero */}
      <header className="relative z-10 mx-auto max-w-6xl px-6 pt-10 text-center sm:pt-16">
        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="kr mb-5 text-sm tracking-[0.25em] text-white/50"
        >
          웹툰 속으로 들어가다 — STEP INSIDE THE STORY
        </motion.p>
        <motion.h1
          variants={fadeUp}
          custom={1}
          initial="hidden"
          animate="show"
          className="wv-title font-display text-5xl leading-[0.95] tracking-tight sm:text-7xl lg:text-8xl"
          data-testid="hero-title"
        >
          WebtoonVerse
        </motion.h1>
        <motion.p
          variants={fadeUp}
          custom={2}
          initial="hidden"
          animate="show"
          className="mx-auto mt-7 max-w-xl text-base text-white/60 sm:text-lg"
        >
          Korean webtoons, reborn as walkable worlds. Choose a portal, meet the characters,
          and shape the moment.
        </motion.p>
        <motion.div
          variants={fadeUp}
          custom={3}
          initial="hidden"
          animate="show"
          className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-2 text-xs uppercase tracking-[0.25em] text-white/50"
        >
          <span className="h-2 w-2 animate-pulse rounded-full bg-[#05D9E8]" />
          월드 선택 / Choose your world
        </motion.div>
      </header>

      {/* portals */}
      <section className="relative z-10 mx-auto mt-12 grid max-w-6xl grid-cols-1 gap-7 px-6 md:grid-cols-3 md:gap-8">
        {WORLDS.map((world, i) => (
          <motion.button
            key={world.id}
            variants={fadeUp}
            custom={4 + i}
            initial="hidden"
            animate="show"
            whileHover={{ scale: 1.045 }}
            whileTap={{ scale: 0.99 }}
            onMouseEnter={() => sound.playHover(world.id)}
            onClick={() => onEnterWorld(world.id)}
            data-testid={`portal-${world.id}`}
            className="wv-portal group relative h-[26rem] overflow-hidden rounded-3xl border border-white/10 text-left"
            style={{ "--glow": world.glow }}
          >
            <img
              src={world.cover}
              alt={world.name}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent" />
            <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: world.accent }} />
            {state.visited[world.id] && (
              <div className="absolute right-4 top-4 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/55 backdrop-blur-sm" data-testid={`visited-${world.id}`}>
                <Check className="h-4 w-4 text-emerald-400" />
              </div>
            )}
            <div className="absolute inset-0 flex flex-col justify-end p-7">
              <span
                className="mb-3 w-fit rounded-full border px-3 py-1 text-[0.65rem] uppercase tracking-[0.2em]"
                style={{ borderColor: world.accentSoft, color: world.accent }}
              >
                {world.genre}
              </span>
              <h3 className="font-display text-2xl tracking-tight text-white">{world.name}</h3>
              <p className="kr mt-1 text-sm" style={{ color: world.accent }}>
                {world.koreanName}
              </p>
              <p className="mt-3 text-sm text-white/60">{world.tagline}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-white opacity-0 transition-all duration-300 group-hover:opacity-100">
                Enter world <ArrowUpRight className="h-4 w-4" />
              </span>
            </div>
          </motion.button>
        ))}
      </section>

      {/* hidden 4th portal — unlocked at 100% */}
      <section className="relative z-10 mx-auto mt-7 max-w-6xl px-6 pb-24">
        <AnimatePresence>
          {state.authorUnlocked && (
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.01 }}
              onMouseEnter={() => sound.playHover("academy")}
              onClick={onEnterStudio}
              data-testid="portal-studio"
              className="wv-portal group relative flex w-full items-center gap-5 overflow-hidden rounded-3xl border p-7 text-left"
              style={{ borderColor: `${AUTHOR_STUDIO.accent}55`, "--glow": AUTHOR_STUDIO.glow, background: `${AUTHOR_STUDIO.accent}10` }}
            >
              <div
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2"
                style={{ borderColor: AUTHOR_STUDIO.accent, boxShadow: `0 0 24px ${AUTHOR_STUDIO.accentSoft}` }}
              >
                <PenTool className="h-6 w-6" style={{ color: AUTHOR_STUDIO.accent }} />
              </div>
              <div>
                <h3 className="font-display text-xl tracking-tight text-white">
                  {AUTHOR_STUDIO.name}{" "}
                  <span className="kr text-sm font-normal" style={{ color: AUTHOR_STUDIO.accent }}>
                    {AUTHOR_STUDIO.koreanName}
                  </span>
                </h3>
                <p className="mt-1 text-sm text-white/60">A hidden room — a message from the creator awaits you.</p>
              </div>
              <ArrowUpRight className="ml-auto h-5 w-5 text-white/60" style={{ color: AUTHOR_STUDIO.accent }} />
            </motion.button>
          )}
        </AnimatePresence>
      </section>

      <AnimatePresence>
        {showCinematic && (
          <FinalCinematic
            onClose={markCinematicSeen}
            onEnter={() => {
              markCinematicSeen();
              onEnterStudio();
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Lobby;
